import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { motion } from 'framer-motion';
// project imports
import NavItem from '../NavItem';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const mode = useSelector((state) => state.theme.mode);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const handleClick = () => {
        setOpen(!open);
        setSelected(!selected ? menu.id : null);
    };
    const isNew = menu.isNew || false;
    const { pathname } = useLocation();
    const checkOpenForParent = (child, id) => {
        child.forEach((item) => {
            if (item.url === pathname) {
                setOpen(true);
                setSelected(id);
            }
        });
    };

    // menu collapse for sub-levels
    useEffect(() => {
        // setOpen(false);
        setSelected(null);
        if (menu.children) {
            menu.children.forEach((item) => {
                if (item.children?.length) {
                    checkOpenForParent(item.children, menu.id);
                }
                if (item.url === pathname) {
                    setSelected(menu.id);
                    setOpen(true);
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, menu.children]);

    // menu collapse & item
    const menus = menu.children?.filter(Boolean)?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = menu.icon ? (
        <Icon strokeWidth={1.5} size="1.3rem" style={{ marginTop: 'auto', marginBottom: 'auto', color: mode === 'dark' ? 'gold' : '' }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selected === menu.id ? 8 : 6,
                height: selected === menu.id ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    return (
        <>
            <ListItemButton
                sx={{
                    borderRadius: `${customization.borderRadius}px`,
                    mb: 0.5,
                    alignItems: 'flex-start',
                    backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                    py: level > 1 ? 1 : 1.25,
                    pl: `${level * 24}px`,
                    '&:hover': {
                        backgroundColor: mode === 'dark' ? '#1f1f1f' : ''
                    }
                }}
                selected={selected === menu.id}
                onClick={handleClick}
            >
                <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}>{menuIcon}</ListItemIcon>
                <ListItemText
                    primary={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                                variant={selected === menu.id ? 'h5' : 'body1'}
                                color={
                                    mode === 'dark' && selected === menu.id
                                        ? 'linear-gradient(to bottom, #1f1f1f, #114568, #0c507d, #0071bc)'
                                        : mode === 'dark'
                                        ? '#fff'
                                        : 'inherit'
                                }
                                sx={{ my: 'auto' }}
                            >
                                {menu.title}
                            </Typography>
                            {isNew && (
                                <motion.div
                                    initial={{ rotate: -5, scale: 0.8, opacity: 0 }}
                                    animate={{ rotate: [-5, 5, -2, 2, 0], scale: 1, opacity: 1 }}
                                    transition={{
                                        duration: 1,
                                        ease: 'easeOut',
                                        type: 'spring',
                                        stiffness: 150,
                                        damping: 10
                                    }}
                                    style={{ display: 'inline-block' }}
                                >
                                    <Chip
                                        label="New"
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.6px',
                                            textTransform: 'uppercase',
                                            borderRadius: '999px',
                                            px: 1.6,
                                            height: '22px',
                                            color: '#ffd700',
                                            background: 'linear-gradient(120deg, #034285ff, #191fc2ff, #2107c9ff)',
                                            backgroundSize: '200% 200%',
                                            animation: 'shimmer 3s ease-in-out infinite, heartbeat 1.5s infinite ease-in-out',
                                            boxShadow: '0 4px 12px rgba(0, 123, 255, 0.4)',
                                            '& .MuiChip-label': {
                                                padding: 0
                                            },
                                            '@keyframes shimmer': {
                                                '0%': { backgroundPosition: '0% 50%' },
                                                '50%': { backgroundPosition: '100% 50%' },
                                                '100%': { backgroundPosition: '0% 50%' }
                                            },
                                            '@keyframes heartbeat': {
                                                '0%, 100%': {
                                                    transform: 'scale(1)'
                                                },
                                                '14%': {
                                                    transform: 'scale(1.15)'
                                                },
                                                '28%': {
                                                    transform: 'scale(1)'
                                                },
                                                '42%': {
                                                    transform: 'scale(1.1)'
                                                },
                                                '70%': {
                                                    transform: 'scale(1)'
                                                }
                                            }
                                        }}
                                    />
                                </motion.div>
                            )}
                        </Box>
                    }
                    secondary={
                        menu.caption && (
                            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {open ? (
                    <IconChevronUp stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
                ) : (
                    <IconChevronDown stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
                )}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        position: 'relative',
                        '&:after': {
                            content: "''",
                            position: 'absolute',
                            left: '32px',
                            top: 0,
                            height: '100%',
                            width: '1px',
                            opacity: 1,
                            background: theme.palette.primary.light
                        }
                    }}
                >
                    {menus}
                </List>
            </Collapse>
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number
};

export default NavCollapse;
